/**
 * Identité de l'appareil.
 *
 * L'identifiant est généré au premier lancement et conservé en `localStorage`.
 * Il ne change jamais : c'est lui qui rattache « Ajouté par … » à un téléphone.
 */

import type { Platform } from '@shared/types.ts';

const DEVICE_ID_KEY = 'todofamille.device_id';
const DEVICE_LABEL_KEY = 'todofamille.device_label';

export function deviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;

  const created = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, created);
  return created;
}

/** Le label est aussi conservé localement pour l'afficher avant la première synchro. */
export function localDeviceLabel(): string | null {
  return localStorage.getItem(DEVICE_LABEL_KEY);
}

export function rememberDeviceLabel(label: string): void {
  localStorage.setItem(DEVICE_LABEL_KEY, label);
}

/**
 * Détecte la plateforme.
 *
 * ⚠ Le résultat est une **suggestion initiale**, utilisée une seule fois à
 * l'inscription de l'appareil. Elle n'est jamais réévaluée par la suite : c'est
 * le label saisi par l'utilisateur qui fait foi, et une détection rejouée à
 * chaque requête finirait par produire des incohérences (mise à jour du
 * navigateur, mode bureau, etc.).
 */
export function detectPlatform(): Platform {
  // Chromium/Android : information structurée, fiable quand elle existe.
  const structured = (
    navigator as Navigator & { userAgentData?: { platform?: string } }
  ).userAgentData?.platform;

  if (structured) {
    const normalised = structured.toLowerCase();
    if (normalised.includes('android')) return 'android';
    if (normalised.includes('ios')) return 'ios';
    // macOS, Windows, Linux, Chrome OS : « other ». On ne devine pas au-delà.
    return 'other';
  }

  // Repli User-Agent, pour Safari qui n'expose pas userAgentData.
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';

  // iPadOS s'annonce comme un Mac depuis iOS 13. Le tactile le distingue d'un
  // vrai Mac, qui rapporte maxTouchPoints à 0.
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';

  return 'other';
}

/** Nom proposé à l'utilisateur, qu'il reste libre de remplacer. */
export function suggestedLabel(platform: Platform = detectPlatform()): string {
  const ua = navigator.userAgent;

  if (platform === 'ios') return /ipad/i.test(ua) || navigator.platform === 'MacIntel' ? 'iPad' : 'iPhone';
  if (platform === 'android') return /pixel/i.test(ua) ? 'Pixel' : 'Téléphone Android';

  return 'Ordinateur';
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari iOS n'implémente pas display-mode et expose ce drapeau à la place.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  return detectPlatform() === 'ios';
}
